// PRD.284 — Format an encounter's vitals into a compact one-line summary for
// the printed prescription (e.g. "Blood Pressure 120/80 mmHg · Pulse 78 bpm ·
// Temp 37.2 °C"). Vitals are definition-driven (any set of vital types), so this
// stays generic: latest value per definition, ordered by the definition's
// sort_order.
//
// Self-contained (no PowerSync/query imports) so the pure logic is unit-testable.

/** Minimal structural shape of a vitals row (from qVitalsByEncounterId). */
export interface VitalSummaryEntry {
  vital_definition_id: string;
  code?: string | null;
  name?: string | null;
  unit?: string | null;
  value_number?: number | null;
  value_text?: string | null;
  value_boolean?: number | null;
  sort_order?: number | null;
  recorded_at?: string | null;
}

function formatValue(e: VitalSummaryEntry): string {
  if (e.value_number != null && Number.isFinite(e.value_number)) {
    // Trim trailing zeros: 37.20 → "37.2", 78 → "78".
    return String(Number(e.value_number));
  }
  if (typeof e.value_text === 'string' && e.value_text.trim()) return e.value_text.trim();
  if (e.value_boolean != null) return e.value_boolean === 1 ? 'Yes' : 'No';
  return '';
}

/**
 * Build a single-line vitals summary. Keeps only the most recently recorded
 * entry per vital definition, orders by `sort_order`, and joins non-empty
 * values with " · ". Returns '' when there are no usable vitals.
 */
export function formatVitalsSummary(rows: VitalSummaryEntry[]): string {
  // Latest entry per definition. Rows may arrive in any order, so compare
  // recorded_at rather than relying on the query's DESC ordering.
  const latest = new Map<string, VitalSummaryEntry>();
  for (const r of rows) {
    const key = r.vital_definition_id || r.code || '';
    if (!key) continue;
    const existing = latest.get(key);
    if (!existing || (r.recorded_at ?? '') > (existing.recorded_at ?? '')) {
      latest.set(key, r);
    }
  }

  return [...latest.values()]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((e) => {
      const value = formatValue(e);
      if (!value) return '';
      const label = (e.name || e.code || '').trim();
      const unit = (e.unit ?? '').trim();
      return `${label ? `${label} ` : ''}${value}${unit ? ` ${unit}` : ''}`.trim();
    })
    .filter(Boolean)
    .join(' · ');
}

// ─── PRD.320 — named vitals for the Dental Structured strip ───

/** The five vitals the Dental Structured strip has dedicated columns for. */
export interface PrintVitalsStrip {
  height?: string;
  weight?: string;
  bloodPressure?: string;
  pulse?: string;
  spo2?: string;
}

/**
 * Which stored vital feeds each strip column.
 *
 * Vitals are DEFINITION-DRIVEN — clinics name and order their own — so this
 * matches on the definition's `code` first and falls back to a loose name
 * match. Anything unrecognised is simply not shown in the strip; it still
 * appears in `formatVitalsSummary`, so no recorded vital is ever lost, it just
 * has no dedicated column.
 */
const STRIP_MATCHERS: { key: keyof PrintVitalsStrip; codes: string[]; nameHints: string[] }[] = [
  { key: 'height', codes: ['height', 'ht'], nameHints: ['height'] },
  { key: 'weight', codes: ['weight', 'wt'], nameHints: ['weight'] },
  {
    // A clinic that stores BP as ONE definition (e.g. text "120/80"). Clinics
    // that store it as separate systolic/diastolic definitions — the seeded
    // default, see `qEncounterVitalsSnapshot` — are handled after this loop.
    key: 'bloodPressure',
    codes: ['bp', 'blood_pressure', 'bloodpressure'],
    nameHints: ['blood pressure'],
  },
  {
    key: 'pulse',
    codes: ['pulse', 'hr', 'heart_rate', 'pulse_rate'],
    nameHints: ['pulse', 'heart rate'],
  },
  {
    key: 'spo2',
    codes: ['spo2', 'sao2', 'o2sat', 'oxygen_saturation'],
    nameHints: ['spo2', 'oxygen'],
  },
];

/**
 * The two halves of a split blood-pressure definition.
 *
 * The seeded defaults record BP as SEPARATE `systolic` and `diastolic` vitals
 * (see `qEncounterVitalsSnapshot`), so neither half matches a `bloodPressure`
 * matcher on its own and the printed BP column stayed empty even though both
 * values were recorded.
 */
const BP_PARTS: { key: 'systolic' | 'diastolic'; codes: string[]; nameHints: string[] }[] = [
  { key: 'systolic', codes: ['systolic', 'bp_systolic', 'sbp'], nameHints: ['systolic'] },
  { key: 'diastolic', codes: ['diastolic', 'bp_diastolic', 'dbp'], nameHints: ['diastolic'] },
];

/**
 * Pick the five named vitals out of an encounter's entries.
 *
 * Keeps the most recently recorded value per definition, exactly as
 * `formatVitalsSummary` does, and appends the unit when the definition has one.
 * Absent vitals are left undefined so the template can render `—` per column
 * while still hiding the whole strip when nothing at all was recorded.
 */
export function buildPrintVitals(rows: readonly VitalSummaryEntry[]): PrintVitalsStrip {
  const latest = new Map<string, VitalSummaryEntry>();
  for (const r of rows) {
    const key = r.vital_definition_id || r.code || '';
    if (!key) continue;
    const existing = latest.get(key);
    if (!existing || (r.recorded_at ?? '') > (existing.recorded_at ?? '')) latest.set(key, r);
  }

  const out: PrintVitalsStrip = {};
  // Halves of a split BP definition, collected as we go and combined below.
  const bp: Partial<Record<'systolic' | 'diastolic', { value: string; unit: string }>> = {};

  for (const e of latest.values()) {
    const value = formatValue(e);
    if (!value) continue;
    const code = (e.code ?? '').trim().toLowerCase();
    const name = (e.name ?? '').trim().toLowerCase();
    const unit = (e.unit ?? '').trim();

    // Systolic/diastolic are checked FIRST: 'diastolic' contains no hint used
    // by the strip matchers, but keeping the order explicit stops a future
    // matcher from swallowing half a blood pressure.
    const part = BP_PARTS.find(
      (m) => m.codes.includes(code) || m.nameHints.some((h) => name.includes(h)),
    );
    if (part) {
      if (!bp[part.key]) bp[part.key] = { value, unit };
      continue;
    }

    const match = STRIP_MATCHERS.find(
      (m) => m.codes.includes(code) || m.nameHints.some((h) => name.includes(h)),
    );
    if (!match || out[match.key]) continue;
    out[match.key] = unit ? `${value} ${unit}` : value;
  }

  // Compose the BP column from the halves, unless a single combined BP
  // definition already filled it — an explicit "120/80" entry wins.
  if (!out.bloodPressure && (bp.systolic || bp.diastolic)) {
    // A recorded half is shown with `—` for the missing one rather than alone:
    // a bare "120 mmHg" in a BP column reads as a complete reading.
    const systolic = bp.systolic?.value ?? '—';
    const diastolic = bp.diastolic?.value ?? '—';
    // One unit for the pair — both halves carry the same one (mmHg).
    const unit = (bp.systolic?.unit || bp.diastolic?.unit || '').trim();
    out.bloodPressure = `${systolic}/${diastolic}${unit ? ` ${unit}` : ''}`;
  }

  return out;
}
